import {
  Address,
  Asset,
  Keypair,
  Networks,
  Transaction,
  TransactionBuilder,
  nativeToScVal,
  scValToNative,
  rpc,
  StrKey,
  xdr,
} from "@stellar/stellar-sdk";
import { Buffer } from "buffer";

export type DepositContext = {
  source: string;
  contractId: string;
  depositStroops: string;
  noteCommitment: string;
};
export type DepositDraft = {
  unsignedXdr: string;
  hash: string;
  fee: string;
  expiresAt: number;
  network: string;
};
export type LiveHealth = {
  ok: true;
  transactionsEnabled: true;
  network: "Stellar Testnet";
  proverReady: true;
  contractReady: true;
  sponsorReady: true;
  contractId: string;
  parameterFamily: "UNTRUSTED_DEVELOPMENT_STELLAR_V2";
  wasmHash: string;
};
export const MAX_DEPOSIT_FEE = 50_000_000n;
const need: (condition: unknown, message: string) => asserts condition = (
  condition,
  message,
) => {
  if (!condition) throw new Error(message);
};
const eq = (a: xdr.ScVal, b: xdr.ScVal) => a.toXDR().equals(b.toXDR());

export function inspectHealth(
  raw: unknown,
  contract: string,
  wasmHash: string,
): LiveHealth {
  const v = raw as Partial<LiveHealth> | null;
  need(
    StrKey.isValidContract(contract) && /^[a-f0-9]{64}$/.test(wasmHash),
    "The live frontend must pin the reviewed contract and bytecode before enabling preparation.",
  );
  need(
    v &&
      v.ok === true &&
      v.transactionsEnabled === true &&
      v.network === "Stellar Testnet" &&
      v.proverReady === true &&
      v.contractReady === true &&
      v.sponsorReady === true &&
      v.contractId === contract &&
      v.parameterFamily === "UNTRUSTED_DEVELOPMENT_STELLAR_V2" &&
      v.wasmHash === wasmHash,
    "The local service is not healthy for the reviewed Testnet deployment.",
  );
  return v as LiveHealth;
}
export function inspectDeposit(
  d: DepositDraft,
  s: DepositContext,
  contract: string,
  now = Date.now(),
  depositContract = contract,
): Transaction {
  need(
    s.contractId === contract &&
      StrKey.isValidContract(contract) &&
      StrKey.isValidEd25519PublicKey(s.source),
    "Deposit session identity differs from the reviewed contract.",
  );
  need(
    typeof s.depositStroops === "string" &&
      /^[1-9][0-9]*$/.test(s.depositStroops) &&
      BigInt(s.depositStroops) <= 1000000000n &&
      /^[a-f0-9]{64}$/.test(s.noteCommitment),
    "Unsupported deposit amount or note encoding.",
  );
  need(
    d.network === "Stellar Testnet" &&
      /^[a-f0-9]{64}$/.test(d.hash) &&
      /^[1-9][0-9]*$/.test(d.fee),
    "Invalid Testnet deposit preparation.",
  );
  need(
    Number.isSafeInteger(d.expiresAt) &&
      d.expiresAt * 1000 > now + 5_000 &&
      d.expiresAt * 1000 <= now + 15 * 60_000,
    "Deposit expired or has excessive validity.",
  );
  need(
    typeof d.unsignedXdr === "string" && d.unsignedXdr.length < 250000,
    "Invalid deposit XDR size.",
  );
  const t = TransactionBuilder.fromXDR(d.unsignedXdr, Networks.TESTNET);
  need(
    t instanceof Transaction &&
      t.toXDR() === d.unsignedXdr &&
      t.source === s.source &&
      t.signatures.length === 0 &&
      t.operations.length === 1 &&
      t.memo.type === "none" &&
      (t.extraSigners?.length ?? 0) === 0,
    "Unsafe deposit envelope.",
  );
  need(
    t.timeBounds &&
      Number(t.timeBounds.maxTime) === d.expiresAt &&
      Number(t.timeBounds.minTime) <= now / 1000,
    "Deposit validity differs from preparation.",
  );
  need(
    t.fee === d.fee &&
      BigInt(t.fee) <= MAX_DEPOSIT_FEE &&
      t.hash().toString("hex") === d.hash,
    "Deposit fee or transaction hash differs from the quote.",
  );
  const o = t.operations[0];
  need(
    o.type === "invokeHostFunction" &&
      (!o.source || o.source === s.source) &&
      o.func.switch().name === "hostFunctionTypeInvokeContract",
    "Deposit must invoke the reviewed contract only.",
  );
  const c = o.func.invokeContract();
  const expected = [
    Address.fromString(s.source).toScVal(),
    nativeToScVal(BigInt(s.depositStroops), { type: "i128" }),
    xdr.ScVal.scvBytes(Buffer.from(s.noteCommitment, "hex")),
  ];
  need(
    Address.fromScAddress(c.contractAddress()).toString() === depositContract &&
      c.functionName().toString() === "demo_deposit" &&
      c.args().length === 3 &&
      c.args().every((v, i) => eq(v, expected[i])),
    "Deposit contract, method, account, amount or commitment changed.",
  );
  need(
    o.auth?.length === 1 &&
      o.auth[0].credentials().switch().name ===
        "sorobanCredentialsSourceAccount",
    "Unexpected deposit authorization credentials.",
  );
  const invocation = o.auth[0].rootInvocation(),
    f = invocation.function();
  need(
    f.switch().name === "sorobanAuthorizedFunctionTypeContractFn" &&
      Address.fromScAddress(f.contractFn().contractAddress()).toString() === depositContract &&
      f.contractFn().functionName().toString() === "demo_deposit" &&
      f.contractFn().args().length === 3 &&
      f.contractFn().args().every((v, i) => eq(v, expected[i])),
    "Authorization does not match the deposit.",
  );
  need(
    invocation.subInvocations().length === 1,
    "Unexpected nested deposit authorization.",
  );
  const transfer = invocation.subInvocations()[0];
  need(
    transfer.function().switch().name ===
      "sorobanAuthorizedFunctionTypeContractFn",
    "Unexpected delegated authorization.",
  );
  const delegated = transfer.function().contractFn();
  let assetInvocation = transfer;
  if (depositContract !== contract) {
    need(
      Address.fromScAddress(delegated.contractAddress()).toString() === contract &&
        delegated.functionName().toString() === "demo_deposit" &&
        delegated.args().length === 3 &&
        delegated.args().every((v, i) => eq(v, expected[i])) &&
        transfer.subInvocations().length === 1,
      "Adapter does not forward the exact deposit into the pinned pool.",
    );
    assetInvocation = transfer.subInvocations()[0];
  }
  need(assetInvocation.subInvocations().length === 0, "Unexpected nested asset authorization.");
  const tf = assetInvocation.function().contractFn(),
    native = Asset.native().contractId(Networks.TESTNET);
  const args = [
    expected[0],
    Address.fromString(contract).toScVal(),
    expected[1],
  ];
  need(
    Address.fromScAddress(tf.contractAddress()).toString() === native &&
      tf.functionName().toString() === "transfer" &&
      tf.args().length === 3 &&
      tf.args().every((v, i) => eq(v, args[i])),
    "Authorization contains an unexpected asset transfer.",
  );
  const ext = t.toEnvelope().v1().tx().ext();
  need(ext.switch() === 1, "Missing simulated Soroban resources.");
  const data = ext.sorobanData(),
    r = data.resources();
  need(
    data.resourceFee().toBigInt() >= 0n &&
      data.resourceFee().toBigInt() + 100n <= BigInt(t.fee) &&
      r.instructions() <= 400_000_000 &&
      r.diskReadBytes() <= 200_000 &&
      r.writeBytes() <= 200_000 &&
      r.footprint().readOnly().length + r.footprint().readWrite().length <= 64,
    "Excessive deposit resources.",
  );
  return t;
}
export function inspectSignedDeposit(
  signedXdr: string,
  d: DepositDraft,
  s: DepositContext,
  contract: string,
  now = Date.now(),
  depositContract = contract,
): string {
  const expected = inspectDeposit(d, s, contract, now, depositContract),
    actual = TransactionBuilder.fromXDR(signedXdr, Networks.TESTNET);
  need(
    actual instanceof Transaction &&
      actual.toXDR() === signedXdr &&
      actual.signatureBase().equals(expected.signatureBase()) &&
      actual.signatures.length === 1,
    "Freighter returned an altered deposit.",
  );
  const key = Keypair.fromPublicKey(s.source),
    signature = actual.signatures[0];
  need(
    signature.hint().equals(key.signatureHint()) &&
      key.verify(actual.hash(), signature.signature()),
    "Freighter signature does not authorize this deposit.",
  );
  return signedXdr;
}
export async function verifyConfirmed(
  hash: string,
  contract: string,
  method: "demo_deposit" | "demo_settle",
  source?: string,
  expected?: {
    recipient?: string;
    withdrawStroops?: string;
    feesStroops?: string;
    depositStroops?: string;
    noteCommitment?: string;
  },
): Promise<number> {
  need(/^[a-f0-9]{64}$/.test(hash), "Missing transaction hash.");
  const r = await new rpc.Server("https://soroban-testnet.stellar.org", {
    timeout: 15000,
  }).getTransaction(hash);
  need(
    r.status === "SUCCESS" && Number.isSafeInteger(r.ledger) && r.ledger > 0,
    "Stellar RPC has not confirmed this transaction.",
  );
  const t = TransactionBuilder.fromXDR(
    r.envelopeXdr.toXDR("base64"),
    Networks.TESTNET,
  );
  need(
    t instanceof Transaction &&
      t.hash().toString("hex") === hash &&
      (!source || t.source === source) &&
      t.operations.length === 1,
    "Confirmed transaction identity mismatch.",
  );
  const o = t.operations[0];
  need(
    o.type === "invokeHostFunction" &&
      o.func.switch().name === "hostFunctionTypeInvokeContract",
    "Unexpected confirmed operation.",
  );
  const c = o.func.invokeContract();
  need(
    Address.fromScAddress(c.contractAddress()).toString() === contract &&
      c.functionName().toString() === method,
    "Confirmed transaction targets another contract or method.",
  );
  if (method === "demo_deposit" && expected) {
    need(
      source &&
        expected.depositStroops &&
        expected.noteCommitment &&
        c.args().length === 3,
      "Missing deposit confirmation context.",
    );
    const args = [
      Address.fromString(source).toScVal(),
      nativeToScVal(BigInt(expected.depositStroops), { type: "i128" }),
      xdr.ScVal.scvBytes(Buffer.from(expected.noteCommitment, "hex")),
    ];
    need(
      c.args().every((v, i) => eq(v, args[i])),
      "Confirmed deposit differs from the approved source, amount or commitment.",
    );
  }
  if (method === "demo_settle" && expected) {
    need(
      expected.recipient &&
        expected.withdrawStroops &&
        expected.feesStroops &&
        c.args().length === 2 &&
        eq(c.args()[1], Address.fromString(expected.recipient).toScVal()),
      "Confirmed withdrawal pays a different recipient.",
    );
    need(
      r.returnValue,
      "Authoritative transaction has no contract return value.",
    );
    const receipt = scValToNative(r.returnValue) as Record<string, unknown>;
    need(
      receipt &&
        String(receipt.recipient_amount) === expected.withdrawStroops &&
        String(receipt.fees) === expected.feesStroops &&
        Number(receipt.ledger) === r.ledger,
      "Authoritative withdrawal receipt has an unexpected payout or fee.",
    );
  }
  return r.ledger;
}

/** Checks the complete relayer envelope against the locally generated public proof. */
export function inspectPrivateQuote(
  quote: any,
  expected: {
    id: string;
    bundle: any;
    recipient: string;
    operator: string;
    feeCap?: string;
    allowExpired?: boolean;
    contract: string;
  },
  now = Date.now(),
): Transaction {
  need(
    quote.network === "Stellar Testnet" &&
      quote.id === expected.id &&
      /^[a-f0-9]{64}$/.test(quote.hash),
    "Private quote identity mismatch.",
  );
  need(
    typeof quote.unsignedXdr === "string" && quote.unsignedXdr.length < 250000,
    "Invalid private quote size.",
  );
  const tx = TransactionBuilder.fromXDR(quote.unsignedXdr, Networks.TESTNET);
  need(
    tx instanceof Transaction &&
      tx.toXDR() === quote.unsignedXdr &&
      tx.hash().toString("hex") === quote.hash &&
      tx.source === expected.operator &&
      tx.operations.length === 1 &&
      tx.signatures.length === 0 &&
      tx.fee === quote.fee &&
      BigInt(tx.fee) <= BigInt(expected.feeCap ?? "200000000") &&
      BigInt(expected.feeCap ?? "200000000") <= 600000000n,
    "Invalid private network fee quote.",
  );
  need(
    tx.memo.type === "none" &&
      !tx.extraSigners?.length &&
      Number(tx.timeBounds?.minTime) <= now / 1000 &&
      Number(tx.timeBounds?.maxTime) === quote.expiresAt &&
      Number.isSafeInteger(quote.expiresAt) && quote.expiresAt > 0 &&
      (expected.allowExpired || quote.expiresAt * 1000 > now + 5000) &&
      quote.expiresAt * 1000 <= now + 900000,
    "Expired or unsafe private quote.",
  );
  const op = tx.operations[0];
  need(
    op.type === "invokeHostFunction" &&
      !op.source &&
      op.func.switch().name === "hostFunctionTypeInvokeContract",
    "Unexpected quoted operation.",
  );
  const call = op.func.invokeContract();
  need(
    Address.fromScAddress(call.contractAddress()).toString() ===
      expected.contract &&
      call.functionName().toString() === "demo_settle" &&
      call.args().length === 2 &&
      !op.auth?.length,
    "Unexpected private settlement.",
  );
  const plain = (value: any): any =>
    value instanceof Uint8Array
      ? Buffer.from(value).toString("hex")
      : Array.isArray(value)
        ? value.map(plain)
        : value && typeof value === "object"
          ? Object.fromEntries(
              Object.entries(value).map(([key, item]) => [key, plain(item)]),
            )
          : value;
  const equal = (a: any, b: any): boolean =>
    typeof a === typeof b &&
    (typeof a === "object" && a !== null
      ? b !== null &&
        Object.keys(a).length === Object.keys(b).length &&
        Object.keys(a).every((key) => equal(a[key], b[key]))
      : a === b);
  const { family, ...bundle } = expected.bundle;
  need(
    equal(plain(scValToNative(call.args()[0])), bundle) &&
      scValToNative(call.args()[1]) === expected.recipient,
    "Quoted proof or destination changed.",
  );
  return tx;
}
